import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const CaregiverDirectory = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/caregivers/directory`);
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load directory');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sendInAppMessage = (recipient) => {
    navigate(`/messages?to=${recipient.id}`);
  };

  const RAINBOW = ['#dc2626', '#ea580c', '#d97706', '#65a30d', '#16a34a', '#0d9488', '#0891b2', '#2563eb', '#7c3aed', '#c026d3'];
  const colorFor = (name) => RAINBOW[((name || '?').toUpperCase().charCodeAt(0) - 65 + RAINBOW.length * 3) % RAINBOW.length];

  const filtered = (data?.caregivers || []).filter((c) =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.city?.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  return (
    <div className="dashboard-container" data-testid="caregiver-directory-page">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <h1 className="page-title rainbow-text" data-testid="directory-title">
          <i className="fa-solid fa-people-group rainbow-text" style={{ marginRight: 10 }}></i>
          Caregiver Directory
        </h1>
        <p className="page-subtitle" data-testid="directory-area">
          {data?.area ? `Caregivers in ${data.area}` : 'Caregivers near you'} · sorted A–Z
        </p>

        {loading && <div className="loading-screen">Loading caregivers…</div>}

        {!loading && data && !data.available && (
          <div className="directory-empty" data-testid="directory-empty-state">
            <i className="fa-solid fa-seedling directory-empty-icon"></i>
            <h3>{data.count >= 3 ? 'No matches' : 'Building your community'}</h3>
            <p>{data.reason}</p>
            <button
              className="primary-btn"
              data-testid="directory-edit-profile-btn"
              onClick={() => navigate('/profile/edit')}
            >
              Edit My Profile
            </button>
          </div>
        )}

        {!loading && data?.available && (
          <>
            <div className="directory-toolbar">
              <input
                type="search"
                placeholder="Search by name or city…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="directory-search"
                data-testid="directory-search-input"
              />
              <span className="directory-count" data-testid="directory-count">
                {data.count} caregiver{data.count !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="directory-grid">
              {filtered.map((c) => (
                <div className="directory-card" key={c.id} data-testid={`directory-card-${c.id}`} style={{ borderTop: `4px solid ${colorFor(c.name)}` }}>
                  <div className="directory-card-header">
                    {c.caregiver_avatar ? (
                      <img src={c.caregiver_avatar} alt={c.name} className="directory-avatar" style={{ boxShadow: `0 0 0 3px ${colorFor(c.name)}` }} />
                    ) : (
                      <div className="directory-avatar-fallback" style={{ background: colorFor(c.name) }}>
                        {c.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <h3 className="directory-card-name" style={{ color: colorFor(c.name) }}>{c.name}</h3>
                      <p className="directory-card-location">
                        <i className="fa-solid fa-location-dot" />{' '}
                        {[c.city, c.county, c.state].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                  {c.directory_bio && (
                    <p className="directory-card-bio">{c.directory_bio}</p>
                  )}
                  <div className="directory-card-actions">
                    {c.email && (
                      <a
                        href={`mailto:${c.email}`}
                        className="directory-action-btn email"
                        data-testid={`directory-email-${c.id}`}
                        title={`Email ${c.name}`}
                      >
                        <i className="fa-solid fa-envelope" /> Email
                      </a>
                    )}
                    {c.phone && (
                      <a
                        href={`tel:${c.phone}`}
                        className="directory-action-btn phone"
                        data-testid={`directory-phone-${c.id}`}
                        title={`Call ${c.name}`}
                      >
                        <i className="fa-solid fa-phone" /> Call
                      </a>
                    )}
                    <button
                      className="directory-action-btn message"
                      data-testid={`directory-message-${c.id}`}
                      onClick={() => sendInAppMessage(c)}
                      title={`Message ${c.name} in-app`}
                    >
                      <i className="fa-solid fa-comment-dots" /> Message
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <p className="directory-no-results" data-testid="directory-no-results">
                No caregivers match your search.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CaregiverDirectory;
